import { RichText } from '@/components/shared/rich-text'
import { SectionHeader } from '@/components/shared/section-header'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { BlokProps } from '@/lib/types'
import { CommunityStoryblok } from '@/lib/types/component-types-sb'
import { cn } from '@/lib/utils'
import { storyblokEditable } from '@storyblok/react/rsc'
import Link from 'next/link'
import { SiDiscord, SiGithub } from 'react-icons/si'

export function Community({ blok }: BlokProps<CommunityStoryblok>) {
  const shuffledGroups = () => {
    const shuffled = [...(blok.testimonials ?? [])].sort(() => 0.5 - Math.random())
    const groups = Array(4)
      .fill([])
      .map((_, i) => shuffled.filter((_, index) => index % 4 === i))
    return groups
  }
  const testimonialGroups = shuffledGroups()

  return (
    <section
      {...storyblokEditable(blok)}
      className="border-border border-y pt-32"
    >
      <div className="page-container">
        <SectionHeader
          icon={blok.descriptorIcon}
          title={blok.title}
          preTitle={blok.descriptorTitle}
          content={blok.content}
          layout="center"
        ></SectionHeader>
        <div className="mt-9 mb-10 flex flex-col items-center justify-center gap-2.5 lg:flex-row">
          {blok.discordLink?.cached_url && (
            <Button
              asChild
              variant="discord"
            >
              <Link
                target="_blank"
                href={blok.discordLink.cached_url}
              >
                <SiDiscord /> Discord Community
              </Link>
            </Button>
          )}
          {blok.githubDiscussionsLink?.cached_url && (
            <Button
              asChild
              variant="secondary"
            >
              <Link
                target="_blank"
                href={blok.githubDiscussionsLink.cached_url}
              >
                <SiGithub /> GitHub Discussions
              </Link>
            </Button>
          )}
        </div>
      </div>
      <div className="relative flex w-full justify-center overflow-hidden lg:container lg:mx-auto">
        <div className="relative h-[550px] w-[1360px] shrink-0 overflow-hidden lg:w-full">
          <div className="grid grid-cols-4 items-end gap-2.5">
            {testimonialGroups.map((group, i) => {
              function translateClass() {
                switch (i) {
                  case 0:
                    return 'translate-y-0'
                  case 1:
                    return 'translate-y-24'
                  case 2:
                    return 'translate-y-12'
                  default:
                    return null
                }
              }

              return (
                <div
                  key={`testimonial-group-${i}`}
                  className={cn('space-y-2.5', translateClass())}
                >
                  {group.map((testimonial, i) => {
                    return (
                      <div
                        key={testimonial._uid}
                        className="bg-background border-border from-sidebar to-background space-y-4 rounded-lg border bg-gradient-to-b px-7 py-8"
                      >
                        <RichText content={testimonial.content} />
                        <div className="flex items-center gap-2.5">
                          <Avatar>
                            {testimonial.avatar?.filename && (
                              <AvatarImage
                                src={testimonial.avatar.filename}
                                alt={`${testimonial.name} from ${testimonial.company}`}
                              />
                            )}
                            <AvatarFallback>
                              {testimonial.name
                                ?.split(' ')
                                ?.map((p) => p.substring(0, 1))
                                .join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="text-accent-foreground font-medium">
                              {testimonial.name}
                            </div>
                            <div className="text-muted-foreground">
                              {testimonial.role}, {testimonial.company}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
